import React from 'react';
import PropTypes from 'prop-types';

import { currency, percent } from 'Helpers/Formats';
import { isNumeric } from 'Helpers/Types';
import { getMaterialInputPropsWithDefaults } from 'Helpers/Meta';

import HelperText from 'Components/UI/Meta/MetaControl/HelperText';
import { usePromptHint } from 'Components/UI/Meta/MetaControl/Types/usePromptHint';
import { getValue } from '../utils';

// This input can receive either events or values because javascript
const getValueFromInput = (v) => {
  switch (typeof v) {
    case 'object':
      return v?.target?.value;
    default:
    case 'string':
    case 'number':
      return v;
  }
};

function NumberMetaControl({
  attrName,
  helperText: propHelperText,
  onChange,
  onInlineSubmit,
  metaField,
  resource,
  CustomProps,
  SpreadProps,
  HelperProps,
  InputProps: InputPropsProp,
  ...rest
}) {
  const Component = metaField.attr('_component');
  const min = metaField.attr('min');
  const max = metaField.attr('max');
  const precision = metaField.attr('precision');
  const readonly = metaField.attr('readonly');
  const prompt = metaField.attr('prompt') || 'This field';
  const hint = metaField.attr('hint');
  const { label } = usePromptHint(metaField);
  const muiInputProps = metaField.attr('materialInputProps');
  const transformValue = metaField.attr('_transformValue') || null;
  const disregardIsInvalid = metaField.attr('_disregardIsInvalid') || false;
  const { InputProps: customMuiProps = {}, ...restCustomProps } = CustomProps;
  // TODO: remove `roundToDollar`
  const roundToDollar = metaField.attr('roundToDollar') || metaField.attr('precision') === 0;

  const value = getValue(resource, metaField, attrName,
    {
      formatReadOnly: (v) => {
        if (Number.isNaN(parseFloat(v))) {
          return v;
        }

        switch (hint) {
          case 'usd':
            return currency(parseFloat(v), roundToDollar);
          case 'percent': {
            const numberString = metaField.attr('precision') ? parseFloat(v).toFixed(parseInt(metaField.attr('precision'), 10)) : parseFloat(v);
            return percent(numberString);
          }
          default:
            return v;
        }
      },
    });


  let helperText = HelperProps.helperText || propHelperText || '';


  const numValue = parseFloat(value);
  const isInvalid = disregardIsInvalid ? false : numValue < min || numValue > max;

  const getInvalidHelperText = () => {
    let minString;
    let maxString;
    switch (hint) {
      case 'usd':
        minString = currency(min);
        maxString = currency(max);
        break;
      case 'percent':
        minString = `${Math.round(min)}%`;
        maxString = `${Math.round(max)}%`;
        break;
      default:
        minString = min;
        maxString = max;
    }
    if (minString && maxString) {
      return `${prompt} must be between ${minString} and ${maxString}.`;
    }
    if (minString) {
      return `${prompt} must be greater than ${minString}.`;
    }
    return `${prompt} must be less than ${maxString}.`;
  };

  helperText = isInvalid ? getInvalidHelperText() : helperText;

  const InputProps = {
    ...muiInputProps,
    ...customMuiProps,
    ...getMaterialInputPropsWithDefaults(hint, muiInputProps || rest),
    ...InputPropsProp,
    readonly,
  };
  return (
    <Component
      helperText={
        <HelperText
          metaField={metaField}
          {...HelperProps}
          helperText={helperText}
        />
      }
      roundToDollar={roundToDollar}
      required={metaField.attr('required')}
      helperTextValue={helperText}
      error={isInvalid || InputProps.error}
      hidden={metaField.attr('hidden')}
      label={label}
      value={value}
      InputProps={InputProps}
      readonly={readonly}
      onBlur={onInlineSubmit}
      onChange={(v) => {
        let val = getValueFromInput(v);

        // Allow an override to have a chance to manipulate the value before setting it
        if (transformValue) {
          val = transformValue(val);
        }

        if (precision != null) {
          if (typeof val === 'string') {
            if (precision === 0 && val.includes('.')) {
              return null;
            }
            const parts = val.split('.');
            if (parts && parts.length > 1 && parts[1].length > precision) {
              return null;
            }
          }
        }

        if (!val || isNumeric(val)) {
          return onChange(val);
        }
        return null;
      }}
      min={min}
      max={max}
      {...SpreadProps}
      {...restCustomProps}
      {...rest}
    />
  );
}

NumberMetaControl.defaultProps = {
  HelperProps: {},

};

NumberMetaControl.propTypes = {
  HelperProps: PropTypes.object,
  attrName: PropTypes.string.isRequired,
  metaField: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};

export default NumberMetaControl;
